import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/16/solid";
import clsx from "clsx";

const Dropdown = ({
  label,
  placeholder = "Select...",
  value,
  onChange,
  options = [],
  withSearch = false,
  disabled = false,
  loading = false,
  required = false,
  clearable = false,
  error,
  // Infinite scroll props
  hasMore = false,
  onLoadMore,
  loadingMore = false,
  // Search props (API or client-side)
  onSearchChange, // If provided, uses API search; otherwise client-side
  searchDebounceMs = 500,
  searchPlaceholder,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [coords, setCoords] = useState(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Determine search mode
  const isApiSearch = !!onSearchChange;

  // Client-side filtering (only when NOT using API search)
  const filteredOptions = isApiSearch
    ? options // Use all options when API handles search
    : options.filter((opt) =>
        opt.name.toLowerCase().includes(search.toLowerCase())
      );

  const handleSelect = (opt) => {
    setTimeout(() => {
      onChange(opt);
      setIsOpen(false);
    }, 10);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  const handleToggle = (e) => {
    if (disabled) return;
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  // Handle search input with debouncing (works for both modes)
  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearch(value);

    if (isApiSearch) {
      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Debounce API call
      searchTimeoutRef.current = setTimeout(() => {
        onSearchChange(value);
      }, searchDebounceMs);
    }
    // For client-side search, no debounce needed - filtering happens instantly
  };

  // Infinite scroll handler
  useEffect(() => {
    if (!isOpen) return;

    const checkAndLoadMore = () => {
      const dropdown = dropdownRef.current;
      if (!dropdown || !hasMore || !onLoadMore || loadingMore) return;

      const { scrollTop, scrollHeight, clientHeight } = dropdown;

      // Check if dropdown has actual content
      if (scrollHeight === 0 || clientHeight === 0) return;

      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      // Trigger when scrolled past 80% or content doesn't fill the dropdown
      if (scrollPercentage > 0.8 || scrollHeight <= clientHeight) {
        console.log("Loading more...", {
          scrollPercentage,
          scrollHeight,
          clientHeight,
        });
        onLoadMore();
      }
    };

    const handleScroll = () => {
      checkAndLoadMore();
    };

    // Wait for dropdown to be in DOM and rendered
    const setupScrollListener = () => {
      const dropdown = dropdownRef.current;
      if (!dropdown) {
        // Retry if dropdown not ready
        setTimeout(setupScrollListener, 50);
        return;
      }

      dropdown.addEventListener("scroll", handleScroll, { passive: true });

      // Check initial state multiple times
      setTimeout(checkAndLoadMore, 100);
      setTimeout(checkAndLoadMore, 250);
      setTimeout(checkAndLoadMore, 500);
    };

    setupScrollListener();

    return () => {
      const dropdown = dropdownRef.current;
      if (dropdown) {
        dropdown.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isOpen, hasMore, loadingMore, onLoadMore, filteredOptions.length]);

  // Handle outside clicks
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        !document.getElementById("dropdown-portal")?.contains(e.target)
      ) {
        closeTimeoutRef.current = setTimeout(() => setIsOpen(false), 20);
      }
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
      clearTimeout(closeTimeoutRef.current);
      clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  // Reset search when dropdown closes (only for API search)
  useEffect(() => {
    if (!isOpen) {
      if (isApiSearch && search) {
        // Clear search on backend when closing
        setSearch("");
        onSearchChange("");
      } else if (!isApiSearch) {
        // For client-side, just clear the input
        setSearch("");
      }
    }
  }, [isOpen]);

  // Position dropdown
  useLayoutEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = 250;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

      setCoords({
        top: openUp ? rect.top - dropdownHeight - 8 : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  const handleDropdownClick = (e) => e.stopPropagation();

  const dropdownContent = (
    <div
      id="dropdown-portal"
      ref={dropdownRef}
      onClick={handleDropdownClick}
      className="fixed z-[99999] bg-white border border-gray-200 rounded-md shadow-lg max-h-[250px] overflow-y-auto"
      style={{
        top: coords?.top ?? 0,
        left: coords?.left ?? 0,
        width: coords?.width ?? 200,
      }}
    >
      {withSearch && (
        <div className="sticky top-0 bg-white p-2 border-b border-gray-200 z-10">
          <input
            type="text"
            value={search}
            onChange={handleSearchInput}
            placeholder={
              searchPlaceholder || (isApiSearch ? "Search..." : "Filter...")
            }
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-400 outline-none"
            autoFocus
          />
        </div>
      )}

      {loading ? (
        <div className="p-3 text-sm text-gray-500 text-center">Loading...</div>
      ) : filteredOptions.length > 0 ? (
        <>
          {filteredOptions.map((opt) => (
            <div
              key={opt.id}
              onClick={() => handleSelect(opt)}
              className={clsx(
                "px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm",
                value?.id === opt.id && "bg-blue-100 text-blue-700 font-medium"
              )}
            >
              {opt.name}
            </div>
          ))}
          {/* Load more trigger zone - acts as a scroll target */}
          {hasMore && (
            <div className="p-3 text-xs text-gray-400 text-center border-t bg-gray-50">
              {loadingMore ? (
                <span className="text-blue-600">Loading more...</span>
              ) : (
                "Scroll for more..."
              )}
            </div>
          )}
        </>
      ) : (
        <div className="p-3 text-sm text-gray-500 text-center">
          {search ? "No results found" : "No options available"}
        </div>
      )}
    </div>
  );

  return (
    <div className="relative w-full" ref={triggerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <button
        type="button"
        disabled={disabled}
        onClick={handleToggle}
        className={clsx(
          "relative w-full text-left px-3 py-2 rounded-md border text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none",
          disabled ? "cursor-not-allowed bg-gray-100" : "cursor-pointer"
        )}
      >
        <span className={value?.name ? "text-gray-900" : "text-gray-400"}>
          {value?.name || placeholder}
        </span>
        {clearable && value?.name && (
          <span
            className="absolute right-8 inset-y-0 flex items-center pr-2 cursor-pointer"
            onClick={handleClear}
          >
            <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          </span>
        )}
        <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
      </button>

      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}

      {isOpen && coords && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default Dropdown;
